import type { AppId } from './apps'
import { dayjs, today } from './format'
import type { FilterValue, ListParams, RefMap } from './list'
import type { ResolvedField, Resource } from './resource'

/**
 * A report (รายงาน): a resource's records in a period, counted and summed by the grouping the reader picks — a day or a
 * month of a date, or a field's values (a branch, a province, a status) — over the same filters as its list, so each
 * row opens as that list. Written once in `@tnn/domain` beside the resources; the API's summary
 * (`GET /api/r/<app>/<name>/summary`) does the counting for the apps and for an assistant alike (docs/reports.md).
 */

export const periods = ['day', 'month'] as const
export type Period = (typeof periods)[number]

/** Rows of a date by a period, one per day or per month */
const periodFormat: Record<Period, string> = { day: 'YYYY-MM-DD', month: 'YYYY-MM' }

/** One way to group: a field's values (`destBranchId`), or a date field by a period (`bookedAt:month`) */
export interface Grouping {
  field: string
  period?: Period
}

export function parseGrouping(by: string): Grouping {
  const [field = '', period] = by.split(':')
  return period === undefined ? { field } : { field, period: period as Period }
}

const dates = new Set(['date', 'datetime'])
const values = new Set(['select', 'ref', 'text', 'suggest', 'boolean'])

/** Whether records can be grouped so (the API refuses the rest): a value per row, a date by day or month */
export function groupable(field: ResolvedField, period?: Period): boolean {
  if (period !== undefined) return periods.includes(period) && dates.has(field.type)
  return values.has(field.type) || field.type === 'date'
}

/** Whether a field adds up: amounts and counts, not percentages or codes */
export const summable = (field: ResolvedField) => field.type === 'number' || field.type === 'integer' || field.type === 'money'

/** Most groups one summary answers with, the largest first (`more` says some were left out) */
export const MAX_GROUPS = 500

/** What a summary is asked for, beside the list's search and filters: `?by=bookedAt:day&sums=pieces,fee&f.status=…` */
export interface SummaryParams extends Pick<ListParams, 'q' | 'filters'> {
  by: string
  sums: readonly string[]
}

export interface SummaryGroup {
  /** The group's value as the list filters on it (a day `2026-10-30`, a month `2026-10`, a branch's id); null: none */
  key: string | null
  count: number
  sums: Record<string, number>
}

export interface SummaryResult {
  by: string
  /** By date for a period, else the largest count first */
  groups: SummaryGroup[]
  total: { count: number; sums: Record<string, number> }
  /** Names of the records the groups and the filters point to */
  refs: RefMap
  /** More groups than `MAX_GROUPS`: the largest were given */
  more?: boolean
}

export interface ReportConfig {
  /** The app whose reports page shows it, at `/reports/<name>` */
  app: AppId
  name: string
  resource: Resource
  /** The date the period is on */
  date: string
  /** Ways to group, the first one the default */
  groups: readonly string[]
  /** Fields summed beside the count of records, in column order */
  sums: readonly string[]
  /** Filters always applied, not the reader's to take off: only submitted documents */
  where?: Readonly<Record<string, FilterValue>>
  /** Filters offered; default every filter of the resource but the date and `where` */
  filters?: readonly string[]
}

export interface Report extends Omit<ReportConfig, 'where' | 'filters'> {
  /** `<app>.reports.<name>`, also its guide's key */
  key: string
  where: Readonly<Record<string, FilterValue>>
  filters: readonly string[]
}

export function defineReport(config: ReportConfig): Report {
  const { resource } = config
  const key = `${config.app}.reports.${config.name}`
  const fail = (problem: string) => {
    throw new Error(`[${key}] ${problem}`)
  }
  const fieldOf = (name: string) => resource.field(name) ?? fail(`"${name}" is not a field of ${resource.key}`)
  if (!dates.has(fieldOf(config.date).type)) fail(`the date "${config.date}" is not a date`)
  if (!config.groups.length) fail('no grouping')
  for (const by of config.groups) {
    const { field, period } = parseGrouping(by)
    if (!groupable(fieldOf(field), period)) fail(`cannot group by "${by}"`)
  }
  for (const name of config.sums) if (!summable(fieldOf(name))) fail(`"${name}" does not add up`)
  const where = config.where ?? {}
  for (const name of Object.keys(where)) fieldOf(name)
  const filters =
    config.filters ?? resource.fieldList.filter((f) => f.filter && f.name !== config.date && !(f.name in where)).map((f) => f.name)
  for (const name of filters) if (!fieldOf(name).filter) fail(`"${name}" is not a filter`)
  return { ...config, key, where, filters }
}

/** i18n keys of a report's words, in its app's namespace */
export const reportLabels = {
  title: (r: Pick<Report, 'app' | 'name'>) => `${r.app}:reports.${r.name}.title`,
  description: (r: Pick<Report, 'app' | 'name'>) => `${r.app}:reports.${r.name}.description`,
  /** What the records counted are called (บิล); none: the shared word */
  count: (r: Pick<Report, 'app' | 'name'>) => `${r.app}:reports.${r.name}.count`,
  /** A grouping's or a sum's name where the report says it otherwise than the field (the province of an area field); none: the field's */
  field: (r: Pick<Report, 'app' | 'name'>, field: string) => `${r.app}:reports.${r.name}.fields.${field}`,
}

/** A range's end as a day: `today` and `now` are today */
const dayOf = (v: string | undefined) => (v === 'today' || v === 'now' ? today() : v)

/**
 * The filter a group's records are listed with: its value, or its day or month within the period asked for (a month
 * row of a period from the 15th starts on the 15th). None for a group of no value: a list cannot ask for empty.
 */
export function groupFilter(grouping: Grouping, key: string | null, current?: FilterValue): FilterValue | undefined {
  if (key === null) return undefined
  if (!grouping.period) return { op: 'in', values: [key] }
  const start = dayjs(grouping.period === 'month' ? `${key}-01` : key)
  const end = grouping.period === 'month' ? start.endOf('month') : start
  const range = current?.op === 'range' ? current : undefined
  const from = [start.format('YYYY-MM-DD'), dayOf(range?.from)].filter(Boolean).sort().at(-1)
  const to = [end.format('YYYY-MM-DD'), dayOf(range?.to)].filter(Boolean).sort()[0]
  return { op: 'range', from, to }
}

/** Every day or month of a period, oldest first: a report shows the days nothing happened too */
export function periodKeys(period: Period, from: string, to: string): string[] {
  const unit = period === 'month' ? 'month' : 'day'
  const last = dayjs(dayOf(to)).startOf(unit)
  const keys: string[] = []
  for (let at = dayjs(dayOf(from)).startOf(unit); !at.isAfter(last) && keys.length < MAX_GROUPS; at = at.add(1, unit)) {
    keys.push(at.format(periodFormat[period]))
  }
  return keys
}
